import { Logout } from "@mui/icons-material";
import { Divider, ListItemIcon, MenuItem } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "src/api/hooks";
import { api } from "src/api/services/api";
import { theme } from "src/assets/theme";
import { Paths } from "src/constants/paths";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { supabaseClient } from "src/supabase/supabaseClient";

interface Props {
  handleClose: () => void;
  open: boolean;
  anchorEl: HTMLElement | null;
}

const AccountMenu = ({ handleClose, anchorEl, open }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleMyAccount = useCallback(() => {
    navigate("/me");
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    try {
      await supabaseClient.auth.signOut();
      //these calls clear out the cached data from the rtk apis,
      dispatch(api.util.resetApiState());
      navigate(Paths.LOG_IN);
    } catch (err) {
      console.log("Logout failed:", err);
    }
  }, [dispatch, navigate]);

  return (
    <Menu
      sx={{
        left: 54,
      }}
      anchorEl={anchorEl}
      id="account-menu"
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 85,
              right: 145,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      <MenuItem onClick={handleMyAccount}>
        <Avatar /> My account
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <Logout fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  );
};

const UserAvatar = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = useMemo(() => anchorEl != null, [anchorEl]);

  const initials = useClinician(
    (clinician) => `${clinician.first_name?.[0]}${clinician.last_name?.[0]}`,
  );

  return (
    <>
      <Tooltip title={<Typography>Account</Typography>} placement="right">
        <IconButton
          sx={{ marginTop: "auto" }}
          onClick={handleClick}
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              background: theme.palette.primary.main,
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>
      <AccountMenu open={open} handleClose={handleClose} anchorEl={anchorEl} />
    </>
  );
};

export default UserAvatar;
